import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Compass, LineChart, Users, Workflow, CheckCircle2, BrainCircuit, Network, ShieldCheck, ExternalLink, Sparkles } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { MEDIA, PROCESS, OUTCOMES, SERVICES } from "@/lib/site";
import usePageMeta from "@/lib/usePageMeta";

const HIGHLIGHTS = [
  { icon: Compass, title: "Assessment", body: "A clear, honest read on where your delivery system actually is — and what to do first." },
  { icon: Workflow, title: "Roadmap", body: "A sequenced, fundable plan built around business outcomes, not frameworks." },
  { icon: Users, title: "Coaching", body: "Senior leadership coaching for the hard calls, the difficult conversations, and the inflection points." },
  { icon: LineChart, title: "Advisory", body: "Hands-on support through the work — calm, discreet, and consistently present." },
];

const AI_PILLARS = [
  {
    icon: BrainCircuit,
    eyebrow: "Strategy",
    title: "AI Strategy for Executives",
    body: "Separating hype from utility — and translating it into a sequenced, fundable roadmap your board can defend.",
  },
  {
    icon: Workflow,
    eyebrow: "Delivery",
    title: "AI-Driven Delivery Systems",
    body: "Embedding AI agents into existing workflows to clear systemic drag, automate reporting, and shorten cycle time.",
  },
  {
    icon: Network,
    eyebrow: "Orchestration",
    title: "Custom Agent Orchestration",
    body: "Bespoke AI agents built to handle the complex coordination work that quietly stalls transformation programs.",
  },
];

const PARTNERS = [
  {
    id: "sentinel-swift",
    name: "Sentinel Swift",
    initials: "SS",
    icon: ShieldCheck,
    focus: "Specialized & Secure Logistics Infrastructure",
    body:
      "Partnering with Sentinel Swift allows us to integrate high-security, time-sensitive logistics solutions into our organizational transformation roadmaps — ensuring that even the most complex physical delivery chains are as agile as the software systems powering them.",
    url: "https://sentinelswift.com/",
  },
  {
    id: "the-sin-productions",
    name: "THE SIN Productions",
    initials: "TS",
    icon: Sparkles,
    focus: "Creative Direction & Brand Storytelling",
    body:
      "Our partnership with THE SIN Productions infuses high-end creative direction and world-class storytelling into our organizational transformations. Together, we ensure every strategic shift is not just operationally sound — but culturally resonant and designed to inspire confidence at every level.",
    url: "https://www.thesinproductions.com/",
  },
];

export default function HomePage() {
  usePageMeta({
    title: "Organizational Transformations Consulting",
    description:
      "Anna Zavarzina helps executive teams improve delivery, alignment, and leadership effectiveness through practical agile transformation consulting.",
    path: "/",
  });
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 opacity-[0.18]"
          style={{
            backgroundImage: `url(${MEDIA.heroBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white via-white/80 to-white" />

        <div className="container-tight pt-16 md:pt-24 pb-24 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-9">
              <Reveal>
                <div className="flex items-center gap-3 mb-8">
                  <span className="h-px w-10 bg-brand-accent" />
                  <p className="overline">Organizational Transformations Consulting</p>
                </div>
              </Reveal>

              <Reveal delay={0.05}>
                <h1
                  data-testid="home-hero-headline"
                  className="font-display text-[44px] sm:text-6xl lg:text-[88px] leading-[0.98] tracking-[-0.03em] text-brand-ink text-balance"
                >
                  Transformation that
                  <br />
                  <span className="italic font-medium">actually</span> moves the
                  <br />
                  business forward.
                </h1>
              </Reveal>

              <Reveal delay={0.1}>
                <p className="mt-10 max-w-2xl text-lg md:text-xl text-brand-muted leading-relaxed">
                  I help executive teams improve delivery, alignment, and leadership effectiveness — through practical agile transformation work, not theatre. Calm, measurable, and built around your business.
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-3 flex lg:justify-end">
              <Reveal delay={0.2} className="w-full lg:w-auto">
                <div className="flex flex-col gap-4">
                  <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">
                    Book a Discovery Call
                    <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                  </Link>
                  <Link to="/services" data-testid="hero-cta-secondary" className="btn-ghost">
                    View Services
                    <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.3}>
            <div className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-line border border-brand-line">
              {[
                { k: "C-suite", v: "Trusted advisor to senior leadership" },
                { k: "Practical", v: "Outcomes over frameworks" },
                { k: "Confidential", v: "Discretion at every engagement" },
                { k: "Measurable", v: "Progress you can defend" },
              ].map((item) => (
                <div key={item.k} className="bg-white p-6 md:p-8">
                  <p className="font-display text-2xl md:text-3xl font-medium text-brand-ink tracking-tight">{item.k}</p>
                  <p className="mt-2 text-sm text-brand-muted leading-relaxed">{item.v}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHO I HELP */}
      <section className="bg-brand-surface2" data-testid="home-who">
        <div className="container-tight section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="overline">Who I help</p>
                <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-brand-ink text-balance">
                  Executives carrying the weight of change.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:pl-8">
              <Reveal delay={0.05}>
                <p className="text-lg text-brand-muted leading-relaxed">
                  I partner with CEOs, COOs, CIOs, CTOs, and VPs of Product, Technology, Delivery, and Transformation — leaders accountable for outcomes, not just activity.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                  {[
                    "Slow, unpredictable delivery",
                    "Misaligned leadership teams",
                    "Transformation fatigue and skepticism",
                    "Friction between business and technology",
                    "Stalled or under-performing change programs",
                    "Heavy process, light outcomes",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-brand-ink">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-brand-accent" strokeWidth={1.25} />
                      <span className="text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT I HELP WITH */}
      <section className="relative" data-testid="home-what">
        <div className="container-tight section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <Reveal>
                <p className="overline">What I help with</p>
                <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-brand-ink text-balance">
                  Four areas. Real outcomes. No theatre.
                </h2>
                <p className="mt-6 text-base md:text-lg text-brand-muted leading-relaxed max-w-md">
                  Every engagement is shaped to your context — but most of my work touches these four areas.
                </p>
                <div className="mt-10 hidden lg:block">
                  <img
                    src={MEDIA.transformation}
                    alt="Transformation visualization"
                    className="w-full max-w-md object-cover border border-brand-line"
                  />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-brand-line border border-brand-line">
                {HIGHLIGHTS.map((h, i) => (
                  <Reveal key={h.title} delay={i * 0.05} className="bg-white">
                    <div className="p-8 md:p-10 h-full">
                      <h.icon className="h-7 w-7 text-brand-accent" strokeWidth={1.25} />
                      <h3 className="mt-6 font-display text-2xl tracking-tight text-brand-ink">{h.title}</h3>
                      <p className="mt-3 text-[15px] text-brand-muted leading-relaxed">{h.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.2}>
                <Link to="/services" data-testid="home-what-cta" className="mt-10 inline-flex items-center gap-2 link-underline text-sm font-medium">
                  See all services
                  <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* AI & INNOVATION */}
      <section className="bg-white relative overflow-hidden" data-testid="home-ai-innovation">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 grain opacity-40" />
        <div className="container-tight section-pad relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-px w-10 bg-brand-accent" />
                  <p className="overline">AI &amp; Innovation</p>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-brand-ink text-balance">
                  AI as a tool for clarity.
                  <br />
                  <span className="italic font-medium">Not</span> another initiative.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:pt-4">
              <Reveal delay={0.05}>
                <p className="text-lg text-brand-muted leading-relaxed max-w-2xl">
                  Most AI strategies fail the same way most transformations fail — they confuse activity with outcomes. My focus is the opposite: a small number of AI moves, sequenced deliberately, that genuinely reduce friction and free up senior capacity.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-line border border-brand-line">
            {AI_PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08} className="bg-white">
                <div
                  data-testid={`ai-pillar-${i + 1}`}
                  className="group h-full p-8 md:p-10 lg:p-12 flex flex-col transition-colors duration-300 hover:bg-brand-surface3"
                >
                  <div className="flex items-center justify-between mb-10">
                    <span className="relative inline-flex h-12 w-12 items-center justify-center bg-brand-ink text-white">
                      <p.icon className="h-5 w-5" strokeWidth={1.25} />
                      <span aria-hidden="true" className="absolute -top-1 -right-1 h-2 w-2 bg-brand-accent" />
                    </span>
                    <span className="text-[10px] tracking-[0.24em] uppercase text-brand-muted font-semibold">
                      {p.eyebrow}
                    </span>
                  </div>
                  <h3 className="font-display text-xl md:text-2xl tracking-tight text-brand-ink leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-5 text-[15px] text-brand-muted leading-relaxed flex-1">
                    {p.body}
                  </p>
                  <div className="mt-8 h-px w-full bg-brand-line group-hover:bg-brand-accent transition-colors duration-300" />
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 border-t border-brand-line pt-10">
              <p className="text-base text-brand-muted leading-relaxed max-w-xl">
                Curious whether AI belongs anywhere in your current transformation? Let's have a focused conversation about where it could actually help — and where it shouldn't.
              </p>
              <Link
                to="/contact"
                data-testid="ai-cta-book"
                className="btn-primary shrink-0"
              >
                Book an AI Discovery Call
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST / OUTCOMES */}
      <section className="bg-brand-navy text-white relative overflow-hidden" data-testid="home-outcomes">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.25] mix-blend-luminosity"
          style={{
            backgroundImage: `url(${MEDIA.architecture})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-brand-navy via-brand-navy/95 to-brand-navy" />
        <div className="container-tight section-pad relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="overline text-[#E8A487]">Outcomes that matter</p>
                <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white text-balance">
                  Measurable progress.
                  <br />
                  Quiet authority.
                </h2>
                <p className="mt-8 text-lg text-white/70 leading-relaxed max-w-md">
                  Transformation work should leave your organization stronger, calmer, and more capable — not exhausted by another initiative.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10">
                {OUTCOMES.map((o, i) => (
                  <Reveal key={o.metric} delay={i * 0.05}>
                    <li className="bg-brand-navy p-8 md:p-10 h-full">
                      <p className="font-display text-4xl sm:text-5xl tracking-tight text-white">
                        {o.metric}
                      </p>
                      <p className="mt-3 text-[15px] text-white/70 leading-relaxed">{o.label}</p>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* STRATEGIC PARTNERSHIP NETWORK */}
      <section className="bg-brand-surface2" data-testid="home-partnerships">
        <div className="container-tight section-pad">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-px w-10 bg-brand-accent" />
                  <p className="overline">Strategic Partnership Network</p>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-brand-ink text-balance">
                  Selected partners.
                  <br />
                  <span className="italic font-medium">Specific</span> capabilities.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:pt-4">
              <Reveal delay={0.05}>
                <p className="text-lg text-brand-muted leading-relaxed max-w-2xl">
                  Some transformation work reaches beyond software and operating model. Our partner network exists for those moments — a curated set of specialist firms we trust to handle the adjacent capabilities that determine whether change actually lands.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-line border border-brand-line">
            {PARTNERS.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08} className="bg-white">
                <article
                  data-testid={`partner-card-${p.id}`}
                  className="h-full p-10 md:p-12 flex flex-col"
                >
                  {/* Logo / Lockup — clickable */}
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`partner-logo-link-${p.id}`}
                    aria-label={`Visit ${p.name} (opens in new tab)`}
                    className="group inline-flex items-center gap-5 self-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-4"
                  >
                    <div className="relative inline-flex h-16 w-16 items-center justify-center bg-brand-ink text-white shrink-0 transition-transform duration-300 group-hover:-translate-y-0.5">
                      <span className="font-display text-xl font-bold tracking-tight">{p.initials}</span>
                      <span aria-hidden="true" className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-brand-accent" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] tracking-[0.24em] uppercase text-brand-muted font-semibold mb-1">
                        Partner
                      </p>
                      <h3 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] leading-none text-brand-ink group-hover:text-brand-accent transition-colors">
                        {p.name}
                      </h3>
                    </div>
                  </a>

                  {/* Focus tag */}
                  <div className="mt-8 inline-flex self-start items-center gap-3 border border-brand-line px-4 py-2.5">
                    <p.icon className="h-4 w-4 text-brand-accent shrink-0" strokeWidth={1.5} />
                    <span className="text-[13px] tracking-wide text-brand-ink">{p.focus}</span>
                  </div>

                  {/* Copy */}
                  <p className="mt-8 text-[15px] text-brand-muted leading-relaxed flex-1">
                    {p.body}
                  </p>

                  {/* Visit link */}
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`partner-link-${p.id}`}
                    className="mt-8 inline-flex self-start items-center gap-2 text-sm font-medium text-brand-accent hover:text-brand-ink transition-colors"
                  >
                    Visit {p.name}
                    <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </a>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 border-t border-brand-line pt-10">
              <p className="text-base text-brand-muted leading-relaxed max-w-xl">
                Building something where a specialist partnership would matter? Let's talk about whether — and how — to bring the right capabilities into the work.
              </p>
              <Link
                to="/contact"
                data-testid="partnerships-cta"
                className="btn-primary shrink-0"
              >
                Inquire About Partnerships
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-white" data-testid="home-process">
        <div className="container-tight section-pad">
          <div className="max-w-2xl">
            <Reveal>
              <p className="overline">The engagement</p>
              <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-brand-ink text-balance">
                A clear, deliberate path.
              </h2>
              <p className="mt-6 text-lg text-brand-muted leading-relaxed">
                Every engagement follows the same disciplined shape — adapted to your scale, urgency, and constraints.
              </p>
            </Reveal>
          </div>

          <div className="mt-20 relative">
            <div className="hidden md:block absolute top-12 left-0 right-0 h-px bg-brand-line" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
              {PROCESS.map((p, i) => (
                <Reveal key={p.step} delay={i * 0.08}>
                  <div className="relative">
                    <div className="flex items-center gap-4">
                      <span className="relative z-10 inline-flex h-12 w-12 items-center justify-center bg-white border border-brand-ink font-display text-base font-semibold text-brand-ink">
                        {p.step}
                      </span>
                      <span className="font-display text-2xl tracking-tight text-brand-ink">{p.name}</span>
                    </div>
                    <p className="mt-6 text-[15px] text-brand-muted leading-relaxed">{p.description}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="bg-brand-surface2" data-testid="home-services-preview">
        <div className="container-tight section-pad">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8 mb-16">
            <Reveal>
              <p className="overline">Services</p>
              <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-brand-ink text-balance max-w-xl">
                Productized engagements, shaped to your context.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/services" data-testid="home-services-cta" className="link-underline text-sm font-medium inline-flex items-center gap-2">
                Explore all services
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-line border border-brand-line">
            {SERVICES.slice(0, 3).map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05} className="bg-white">
                <Link to="/services" className="block h-full p-8 md:p-10 group transition-all duration-300 hover:bg-brand-surface3" data-testid={`home-service-card-${s.id}`}>
                  <p className="number-tag">{s.number}</p>
                  <h3 className="mt-4 font-display text-2xl tracking-tight text-brand-ink">{s.title}</h3>
                  <p className="mt-4 text-[15px] text-brand-muted leading-relaxed">{s.summary}</p>
                  <span className="mt-8 inline-flex items-center gap-2 text-sm text-brand-accent font-medium">
                    Learn more
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-white" data-testid="home-final-cta">
        <div className="container-tight section-pad">
          <div className="border border-brand-ink p-10 md:p-20 relative overflow-hidden">
            <span aria-hidden className="absolute -top-px -right-px h-3 w-3 bg-brand-accent" />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <Reveal>
                  <p className="overline">Next step</p>
                  <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-brand-ink text-balance">
                    Let's start with a focused conversation.
                  </h2>
                  <p className="mt-6 text-lg text-brand-muted leading-relaxed max-w-xl">
                    A short, confidential discovery call — no pitch, no obligation. Just a clear-eyed look at where you are and what's possible.
                  </p>
                </Reveal>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Reveal delay={0.1}>
                  <Link to="/contact" data-testid="home-final-cta-button" className="btn-primary">
                    Book a Discovery Call
                    <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                  </Link>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
